import { createRoot } from 'react-dom/client';
import importAll from "import-all.macro";
import PageWrapper from "./PageWrapper";
import ErrorBoundary from "./ErrorBoundary";
import { StrictMode } from 'react';

declare global {
  interface Window {
    innerHtmls: object;
  }
}

if (window.innerHtmls === undefined) {
  window.innerHtmls = {};
}

export default function setup(startingElementSelector: string) {
  const deferredPageLoaders = {
    ...importAll.deferred("./pages/**/*.tsx"),
    ...importAll.deferred("./pages/**/*.jsx"),
    ...importAll.deferred("./pages/**/*.js"),
    ...importAll.deferred("./pages/*.tsx"),
    ...importAll.deferred("./pages/*.jsx"),
    ...importAll.deferred("./pages/*.js"),
  };

  for (const [path, load] of Object.entries(deferredPageLoaders)) {
    if (typeof load !== "function") {
      continue;
    }

    // Convert ./pages/Distributor/Quotation/Details/index.tsx to distributor-quotation-details
    const normalizedPath = path
      .replace(/^\.\/pages\/(.+)\.(tsx|js|jsx)$/i, "$1")
      .replace(/\//g, "-")
      .replace(/-index$/gi, "")
      .toLowerCase();

    // Check the page for this normalized div
    const divId = `js-react-page-${normalizedPath}`;

    // If it's found, lazy-load the code-split JS and call the index method
    const element = document.querySelector(`${startingElementSelector} #${divId}`) as HTMLElement | null;
    if (element) {
      load().then(({ default: Component }) => {
        const props = (window as any).reactProps || {};

        const componentProps = { ...props, ...element.dataset };
        const root = createRoot(element);
        root.render(
          <StrictMode>
            <PageWrapper {...componentProps}>
              <Component {...componentProps} />
            </PageWrapper>
          </StrictMode>
        );
      });
    }
  }

  const deferredComponentLoaders = {
    ...importAll.deferred("./components/**/*.tsx"),
    ...importAll.deferred("./components/**/*.jsx"),
    ...importAll.deferred("./components/**/*.js"),
    ...importAll.deferred("./components/*.tsx"),
    ...importAll.deferred("./components/*.jsx"),
    ...importAll.deferred("./components/*.js"),
  };

  for (const [path, load] of Object.entries(deferredComponentLoaders)) {
    if (typeof load !== "function") {
      continue;
    }

    // Convert ./pages/Distributor/Quotation/Details/index.tsx to distributor-quotation-details
    const normalizedPath = path
      .replace(/^\.\/components\/(.+)\.(tsx|js|jsx)$/i, "$1")
      .replace(/\//g, "-")
      .replace(/-index$/g, "")
      .toLowerCase();

    // Check the page for this normalized className
    const className = `js-react-component-${normalizedPath}`;

    // If it's found, lazy-load the code-split JS and call the index method
    const elements = document.querySelectorAll(`${startingElementSelector} .${className}`) as NodeListOf<HTMLElement>;
    if (elements && elements.length > 0) {
      load().then(({ default: Component }) => {
        for (let i = 0; i < elements.length; i++) {
          const uid = `${className}-${i}`;
          const element = elements[i];
          const props = (window as any).reactProps || {};

          if (window.innerHtmls[uid] === undefined) {
            window.innerHtmls[uid] = element.innerHTML;
          }

          const componentProps = { ...props, ...element.dataset };
          const innerHtml = window.innerHtmls[uid];
          if (innerHtml) {
            componentProps.dangerouslySetInnerHTML = {
              __html: innerHtml,
            };
          }

          setTimeout(() => {
            const root = createRoot(element);
            root.render(
              <StrictMode>
                <ErrorBoundary>
                  <Component {...componentProps} />
                </ErrorBoundary>
              </StrictMode>
            );
          }, 100);
        }
      });
    }
  }
}
